// src/components/FaqContactForm.js - Enhanced with file attachment capability
import React, { useState, useEffect, useRef } from 'react';
import '../css/FaqContactForm.css';

const FaqContactForm = ({ 
  isOpen, 
  onClose, 
  faqContext = null, // { subject, question } - the FAQ item context
  language = 'nl' 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    houseNumber: '',
    subject: '',
    message: ''
  });
  const [attachments, setAttachments] = useState([]);
  const [showAttachmentError, setShowAttachmentError] = useState(false);
  const [attachmentErrorMessage, setAttachmentErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const fileInputRef = useRef(null);

  // Translations
  const t = {
    nl: {
      title: 'Stel een vraag aan het bestuur',
      name: 'Naam',
      email: 'E-mailadres',
      phone: 'Telefoonnummer',
      houseNumber: 'Huisnummer',
      subject: 'Onderwerp',
      message: 'Uw vraag',
      messagePlaceholder: 'Beschrijf uw vraag zo uitgebreid mogelijk...',
      send: 'Verstuur vraag',
      sending: 'Versturen...',
      cancel: 'Annuleren',
      success: 'Uw vraag is succesvol verstuurd! We nemen zo spoedig mogelijk contact met u op.',
      error: 'Er is een fout opgetreden bij het versturen van uw vraag. Probeer het later opnieuw.',
      required: 'Dit veld is verplicht',
      invalidEmail: 'Voer een geldig e-mailadres in',
      contextNote: 'Deze vraag heeft betrekking op:',
      nameRequired: 'Naam is verplicht',
      emailRequired: 'E-mailadres is verplicht',
      subjectRequired: 'Onderwerp is verplicht',
      messageRequired: 'Vraag is verplicht',
      attachments: 'Bijlagen',
      addAttachments: 'Bijlagen toevoegen',
      fileLimits: 'Max 10MB per bestand',
      filesToAdd: 'Bestanden om toe te voegen',
      removeAttachment: 'Bijlage verwijderen',
      attachmentError: 'De volgende bestanden overschrijden de limiet van 10MB',
      ticketNumber: 'Ticket nummer'
    },
    en: {
      title: 'Ask the board a question',
      name: 'Name',
      email: 'Email address',
      phone: 'Phone number',
      houseNumber: 'House number',
      subject: 'Subject',
      message: 'Your question',
      messagePlaceholder: 'Describe your question in as much detail as possible...',
      send: 'Send question',
      sending: 'Sending...',
      cancel: 'Cancel',
      success: 'Your question has been sent successfully! We will contact you as soon as possible.',
      error: 'An error occurred while sending your question. Please try again later.',
      required: 'This field is required',
      invalidEmail: 'Please enter a valid email address',
      contextNote: 'This question relates to:',
      nameRequired: 'Name is required',
      emailRequired: 'Email address is required',
      subjectRequired: 'Subject is required',
      messageRequired: 'Question is required',
      attachments: 'Attachments',
      addAttachments: 'Add attachments',
      fileLimits: 'Max 10MB per file',
      filesToAdd: 'Files to add',
      removeAttachment: 'Remove attachment',
      attachmentError: 'The following files exceed the 10MB limit',
      ticketNumber: 'Ticket number'
    }
  };

  const translations = t[language];

  // Auto-populate subject when FAQ context is provided
  useEffect(() => {
    if (faqContext && faqContext.question) {
      setFormData(prev => ({
        ...prev,
        subject: `Vraag over: ${faqContext.question.substring(0, 50)}${faqContext.question.length > 50 ? '...' : ''}`
      }));
    }
  }, [faqContext]);

  // Reset form when closing
  useEffect(() => {
    if (!isOpen) {
      // Reset form after closing with a delay to allow for transition effects
      const timer = setTimeout(() => {
        setAttachments([]);
        setShowAttachmentError(false);
        setAttachmentErrorMessage('');
        setSubmitStatus(null);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate files (size limit: 10MB per file)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
    const invalidFiles = files.filter(file => file.size > MAX_FILE_SIZE);
    
    if (invalidFiles.length > 0) {
      setShowAttachmentError(true);
      setAttachmentErrorMessage(`${translations.attachmentError}: ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }
    
    // Add new files to existing attachments
    setAttachments(prev => [...prev, ...files]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Remove an attachment
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  // Validate form
  const validateForm = () => {
    const errors = [];
    
    if (!formData.name.trim()) {
      errors.push(translations.nameRequired);
    }
    
    if (!formData.email.trim()) {
      errors.push(translations.emailRequired);
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push(translations.invalidEmail);
    }
    
    if (!formData.subject.trim()) {
      errors.push(translations.subjectRequired);
    }
    
    if (!formData.message.trim()) {
      errors.push(translations.messageRequired);
    }
    
    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (errors.length > 0) {
      alert(errors.join('\n'));
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      
      // Create FormData object to handle file uploads
      const formDataToSend = new FormData();
      
      // Append form fields
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone || '');
      formDataToSend.append('houseNumber', formData.houseNumber || '');
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);
      
      // Add FAQ context if available
      if (faqContext) {
        formDataToSend.append('faqContext', JSON.stringify(faqContext));
      }
      
      // Add each attachment to the FormData
      attachments.forEach(file => {
        formDataToSend.append('attachments', file);
      });
      
      const response = await fetch(`${API_URL}/api/send-faq-question-with-attachments`, {
        method: 'POST',
        body: formDataToSend,
        // Don't set Content-Type header as the browser will set it correctly with boundary for FormData
      });

      if (response.ok) {
        const responseData = await response.json();
        const ticketNumber = responseData.ticketNumber || 'Unknown';
        
        setSubmitStatus('success');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          houseNumber: '',
          subject: '',
          message: ''
        });
        setAttachments([]);
        
        // Set success message with ticket number
        setSuccessMessage(`${translations.success} ${translations.ticketNumber}: ${ticketNumber}`);
        
        // Close form after 3 seconds
        setTimeout(() => {
          onClose();
          setSubmitStatus(null);
          setSuccessMessage('');
        }, 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send question');
      }
    } catch (error) {
      console.error('Error sending FAQ question:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="faq-form-overlay" onClick={handleClose}>
      <div className="faq-form-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="faq-form-header">
          <h2>{translations.title}</h2>
          <button 
            className="faq-form-close"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* FAQ Context Display */}
        {faqContext && (
          <div className="faq-context-info">
            <p className="context-note">{translations.contextNote}</p>
            <div className="context-details">
              <strong>{faqContext.subject}</strong>
              <em>{faqContext.question}</em>
            </div>
          </div>
        )}

        {/* Success/Error Messages */}
        {submitStatus === 'success' && (
          <div className="faq-form-success">
            <p>{successMessage || translations.success}</p>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="faq-form-error">
            <p>{translations.error}</p>
          </div>
        )}

        {/* Form */}
        <form className="faq-contact-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">{translations.name} *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">{translations.email} *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-row">
            <div className="form-group half">
              <label htmlFor="phone">{translations.phone}</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                disabled={isSubmitting}
              />
            </div>
            
            <div className="form-group half">
              <label htmlFor="houseNumber">{translations.houseNumber}</label>
              <input
                type="text"
                id="houseNumber"
                name="houseNumber"
                value={formData.houseNumber}
                onChange={handleInputChange}
                disabled={isSubmitting}
                placeholder="bijv. 211, 201A"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="subject">{translations.subject} *</label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="message">{translations.message} *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder={translations.messagePlaceholder}
              required
              rows="5"
              disabled={isSubmitting}
            />
          </div>

          {/* Attachments Section */}
          <div className="attachments-section">
            <h3>{translations.attachments}</h3>
            <div className="attachment-uploader">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                multiple
                className="file-input"
                id="faq-attachment-input"
                disabled={isSubmitting}
              />
              <label 
                htmlFor="faq-attachment-input" 
                className={`custom-file-upload ${isSubmitting ? 'disabled' : ''}`}
              >
                <span className="icon">📎</span> {translations.addAttachments}
              </label>
              <span className="file-limits">{translations.fileLimits}</span>
            </div>
            
            {showAttachmentError && (
              <div className="attachment-error">
                <p>{attachmentErrorMessage}</p>
                <button 
                  type="button" 
                  onClick={() => setShowAttachmentError(false)}
                  aria-label="Close error message"
                >
                  ×
                </button>
              </div>
            )}
            
            {attachments.length > 0 && (
              <div className="attachment-list">
                <h4>{translations.filesToAdd} ({attachments.length})</h4>
                <ul>
                  {attachments.map((file, index) => (
                    <li key={index} className="attachment-item">
                      <span className="attachment-icon">📄</span>
                      <span className="attachment-name">{file.name}</span>
                      <span className="attachment-size">({formatFileSize(file.size)})</span>
                      <button 
                        type="button"
                        className="remove-attachment" 
                        onClick={() => removeAttachment(index)}
                        title={translations.removeAttachment}
                        disabled={isSubmitting}
                        aria-label={`${translations.removeAttachment}: ${file.name}`}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button 
              type="button" 
              className="btn-cancel"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {translations.cancel}
            </button>
            <button 
              type="submit"
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? translations.sending : translations.send}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FaqContactForm;